import { useMediaQuery } from '@mui/material';
import { type PaletteColor, useTheme } from '@mui/material/styles';
import type { ArcElement, Chart, Point } from 'chart.js';
import React from 'react';
import { useIntl } from 'react-intl';

import { getHealthScoreInfo } from '@constants/health-score-info';

import type {
  DoughnutChartDataset,
  DoughnutChartOptions,
  DoughnutChartPlugin,
} from './DoughnutChart';
import DoughnutChart from './DoughnutChart';

interface ScoreDoughChartProps {
  /** Health score enum */
  metricId: string;
}

export default function ScoreDoughnutChart({ metricId }: ScoreDoughChartProps) {
  const { formatMessage } = useIntl();
  const { breakpoints, palette } = useTheme();
  const isPhone = useMediaQuery(breakpoints.down(450));
  const isTablet = useMediaQuery(breakpoints.between(451, 700));

  const { chartFactors, data, colors, title } = getHealthScoreInfo(metricId);

  const chartColors = colors.map(({ primary, secondary }) => {
    const colorOption = palette[primary] as PaletteColor;
    const colorKey =
      secondary && colorOption[secondary as keyof PaletteColor]
        ? (secondary as keyof PaletteColor)
        : 'main';
    return colorOption[colorKey];
  });

  const chartData: DoughnutChartDataset = {
    labels: chartFactors.map((factor) => formatMessage({ id: factor })),
    datasets: [
      {
        label: formatMessage(title),
        data,
        backgroundColor: chartColors,
        borderColor: chartColors,
        hoverBackgroundColor: chartColors,
        hoverBorderColor: chartColors,
        hoverOffset: 4,
      },
    ],
  };

  const sliceLabelPlugin: DoughnutChartPlugin = {
    id: 'slice-label-plugin',
    beforeDatasetsDraw(
      chart: Chart<'doughnut', number[], unknown>,
      _args: { cancelable: true },
      _options: Point[]
    ) {
      const { ctx, data } = chart;

      let totalPercentage = 0;
      chart.getDatasetMeta(0).data.forEach((d, index) => {
        const dataPoint = d as ArcElement;
        totalPercentage += data.datasets.at(0)?.data.at(index) ?? 0;
        ctx.save();

        const xCenterPoint = dataPoint.x;
        const yCenterPoint = dataPoint.y;

        const endAngle = dataPoint.endAngle;
        const startAngle = dataPoint.startAngle;
        const centerAngle = (startAngle + endAngle) / 2;

        const outerRadius = dataPoint.outerRadius + 45;
        const xCoordinate = -25 + outerRadius * Math.cos(centerAngle);
        const yCoordinate = -25 + outerRadius * Math.sin(centerAngle);

        const sliceLabel = data.labels?.[index] as string;
        const sliceBgColorArray = data.datasets.at(0)
          ?.backgroundColor as string[];

        ctx.font = 'bold 14px mulish';
        const textWidth = ctx.measureText(sliceLabel).width;
        const centerWidth = textWidth / 2;

        ctx.beginPath();
        ctx.translate(xCenterPoint, yCenterPoint);

        ctx.fillStyle = sliceBgColorArray[index];
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const xAxis =
          xCoordinate +
          (Math.abs(xCoordinate) <= 25
            ? textWidth
            : totalPercentage > 56
              ? 0
              : centerWidth);

        ctx.fillText(sliceLabel, xAxis, yCoordinate + 25);
        ctx.fillText(
          `${data.datasets.at(0)?.data.at(index)}%`,
          xAxis,
          yCoordinate + 45
        );

        ctx.restore();
      });
    },
  };
  const plugins: DoughnutChartPlugin[] = isPhone ? [] : [sliceLabelPlugin];

  const options: DoughnutChartOptions = {
    aspectRatio: isTablet ? 1.5 : 1.2,
    // Pie slice hover will show the slice's label as -> Ex. Resting HR: 12.5%
    formatLabel: ({ formattedValue, label }) => `${label}: ${formattedValue}%`,
    // Pie slice hover will show the slice's title as -> Ex. Immunity index (friendly name of health score)
    formatTitle: (items) => {
      const [{ dataset }] = items;
      return dataset.label ?? '';
    },
  };

  return (
    <DoughnutChart chartData={chartData} options={options} plugins={plugins} />
  );
}
